// base
import { createAsyncThunk } from '@reduxjs/toolkit';

// api
import { riskAssessmentApi, riskAssessmentManagementApi } from '../apis';

// actions
import { actions } from '../actions';

// models
import type {
  ResRiskAssessmentSettingApprovalLine,
  ReqRiskAssessmentSettingApprovalLineDetail,
  ResRiskAssessmentSettingApprovalLineDetail,
  ReqAddRiskAssessmentSettingApprovalLine,
  ReqEditRiskAssessmentSettingApprovalLine,
  ReqApprovalLineBatch,
  ReqRAManagementRegisterProps,
  ReqRAApprovalFormProps,
  ResRAManagementAttachmentsProps
} from '../models';
import type { CommonSearchProps, ResultError, ResultBaseProps } from 'modules/common';
import { postRAMangementRegister } from './riskAssessmentManagement.thunk';
import { patchSubmitRAApproval } from './riskAssessmentApproval.thunk';

/**
 * 위험 평가 설정 결재라인 조회
 */
export const getRiskAssessmentSettingApprovalLine = createAsyncThunk<
  ResRiskAssessmentSettingApprovalLine,
  CommonSearchProps,
  {
    rejectValue: ResultError;
  }
>(
  actions.GET_RISK_ASSESSMENT_SETTING_APPROVAL_LINE,
  async (data: CommonSearchProps, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res: ResRiskAssessmentSettingApprovalLine =
        await riskAssessmentApi.getRiskAssessmentSettingApprovalLine(data);

      return fulfillWithValue(res as ResRiskAssessmentSettingApprovalLine);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/**
 * 위험 평가 설정 결재라인 상세 조회
 * @param {string} riskApprovalLineSettingId
 * @returns {TbmDetailObject}
 */
export const getRiskAssessmentSettingApprovalLineDetail = createAsyncThunk<
  ResRiskAssessmentSettingApprovalLineDetail,
  ReqRiskAssessmentSettingApprovalLineDetail,
  {
    rejectValue: ResultError;
  }
>(
  actions.GET_RISK_ASSESSMENT_SETTING_APPROVAL_LINE_DETAIL,
  async (
    data: ReqRiskAssessmentSettingApprovalLineDetail,
    { fulfillWithValue, rejectWithValue }
  ) => {
    try {
      const res: ResRiskAssessmentSettingApprovalLineDetail =
        await riskAssessmentApi.getRiskAssessmentSettingApprovalLineDetail(data);

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/**
 * 위험 평가 설정 결재라인 생성
 */
export const addRiskAssessmentSettingApprovalLine = createAsyncThunk<
  ResRiskAssessmentSettingApprovalLine,
  ReqAddRiskAssessmentSettingApprovalLine,
  {
    rejectValue: ResultError;
  }
>(
  actions.ADD_RISK_ASSESSMENT_SETTING_APPROVAL_LINE,
  async (
    data: ReqAddRiskAssessmentSettingApprovalLine,
    { dispatch, fulfillWithValue, rejectWithValue }
  ) => {
    try {
      await riskAssessmentApi.addRiskAssessmentSettingApprovalLine(data);

      const res = await dispatch(
        getRiskAssessmentSettingApprovalLine({ page: 0, size: 9999 })
      ).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/**
 * 위험 평가 설정 결재라인 수정
 */
export const updateRiskAssessmentSettingApprovalLine = createAsyncThunk<
  ResRiskAssessmentSettingApprovalLine,
  ReqEditRiskAssessmentSettingApprovalLine,
  {
    rejectValue: ResultError;
  }
>(
  actions.UPDATE_RISK_ASSESSMENT_SETTING_APPROVAL_LINE,
  async (
    data: ReqEditRiskAssessmentSettingApprovalLine,
    { dispatch, fulfillWithValue, rejectWithValue }
  ) => {
    try {
      await riskAssessmentApi.updateRiskAssessmentSettingApprovalLine(data);

      const res = await dispatch(
        getRiskAssessmentSettingApprovalLine({ page: 0, size: 9999 })
      ).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/**
 * 위험 평가 설정 결재라인 삭제
 */
export const deleteRiskAssessmentSettingApprovalLine = createAsyncThunk<
  ResRiskAssessmentSettingApprovalLine,
  string,
  {
    rejectValue: ResultError;
  }
>(
  actions.DELETE_RISK_ASSESSMENT_SETTING_APPROVAL_LINE,
  async (riskApprovalLineSettingId: string, { dispatch, fulfillWithValue, rejectWithValue }) => {
    try {
      await riskAssessmentApi.deleteRiskAssessmentSettingApprovalLine(riskApprovalLineSettingId);

      const res = await dispatch(
        getRiskAssessmentSettingApprovalLine({ page: 0, size: 9999 })
      ).unwrap();

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/** 위험 평가 결재자 상세 조회 */
export const getRiskAssessmentApproverDetail = createAsyncThunk<
  ResRiskAssessmentSettingApprovalLineDetail,
  ReqRiskAssessmentSettingApprovalLineDetail,
  {
    rejectValue: ResultError;
  }
>(
  actions.GET_RISK_ASSESSMENT_APPROVER_DETAIL,
  async (
    data: ReqRiskAssessmentSettingApprovalLineDetail,
    { fulfillWithValue, rejectWithValue }
  ) => {
    try {
      const res: ResRiskAssessmentSettingApprovalLineDetail =
        await riskAssessmentApi.getRiskAssessmentSettingApprovalLineDetail(data);

      return fulfillWithValue(res);
    } catch (error) {
      return rejectWithValue(error as ResultError);
    }
  }
);

/**
 * 위험 평가 설정 결재라인 결재자 일괄 생성
 */
export const addUserApprovalLine = createAsyncThunk<
  void,
  { riskApprovalLineSettingId: string; data: ReqApprovalLineBatch },
  { rejectValue: ResultError }
>(actions.ADD_USER_APPROVAL_LINE, async (data, { rejectWithValue }) => {
  try {
    await riskAssessmentApi.addUserApprovalLine(data.riskApprovalLineSettingId, data.data);
  } catch (error) {
    return rejectWithValue(error as ResultError);
  }
});

/** 위험성 평가서 등록 */
export const submitRiskAssessment = createAsyncThunk<
  void,
  { form: ReqRAManagementRegisterProps; approval: ReqRAApprovalFormProps },
  { rejectValue: ResultError }
>(actions.SUBMIT_RISK_ASSESSMENT, async ({ form, approval }, { dispatch, rejectWithValue }) => {
  try {
    const { resultObject } = await dispatch(postRAMangementRegister(form)).unwrap();

    const { id: riskAssessmentId } = resultObject;

    const newApproval = {
      ...approval,
      submit: {
        ...approval.submit,
        riskAssessmentId
      }
    } as ReqRAApprovalFormProps;

    await dispatch(patchSubmitRAApproval(newApproval)).unwrap();

    return;
  } catch (error) {
    return rejectWithValue(error as ResultError);
  }
});
